<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { uuid } from 'vue3-uuid'
import ImagePicker from '@/onenetwork/components/image_picker/ImagePicker.vue'
import VideoPicker from '@/onenetwork/components/video_picker/VideoPicker.vue'
import { Icon } from '@iconify/vue'

interface MediaLike {
  uuid: string
  name?: string
  url?: {
    original?: string
    medium?: string
    thumbnail?: string
  }
}

interface MediaFormLike {
  initialValues?: {
    photos?: MediaLike[]
    videos?: MediaLike[]
  }
  setFieldValue?: (field: string, value: MediaLike[]) => void
  fields?: {
    photos?: {
      states?: {
        error?: {
          message?: string
        }
      }
    }
    videos?: {
      states?: {
        error?: {
          message?: string
        }
      }
    }
  }
}

interface UploadPayloadLike {
  uuid?: string
  name?: string
  mediaType?: 'image' | 'video'
  file?: {
    type?: string
  }
  url?: {
    original?: string
    medium?: string
    thumbnail?: string
  }
  response?: {
    data?: MediaLike
  }
}

interface Props {
  form: MediaFormLike
  editable: boolean
}

const props = defineProps<Props>()

const photos = ref<MediaLike[]>([])
const videos = ref<MediaLike[]>([])
const imageUuid = ref(uuid.v4().toString())

const initValues = computed(() => props.form?.initialValues || {})

const syncMedia = () => {
  props.form?.setFieldValue('photos', photos.value)
  props.form?.setFieldValue('videos', videos.value)
}

watch(
  () => initValues.value,
  (newVal: { photos?: MediaLike[]; videos?: MediaLike[] }) => {
    photos.value = Array.isArray(newVal?.photos) ? [...newVal.photos] : []
    videos.value = Array.isArray(newVal?.videos) ? [...newVal.videos] : []
    syncMedia()
  },
  { immediate: true, deep: true },
)

const resolveMediaType = (data: UploadPayloadLike) => {
  if (data?.mediaType === 'video' || data?.mediaType === 'image') return data.mediaType
  if (data?.file?.type?.startsWith('video/')) return 'video'
  if (data?.file?.type?.startsWith('image/')) return 'image'
  return 'image'
}

const upsertMedia = (data: UploadPayloadLike) => {
  const media = data?.response?.data || data
  if (!media?.uuid) return

  const mediaType = resolveMediaType(data)
  if (mediaType === 'video') {
    photos.value = photos.value.filter((item) => item.uuid !== media.uuid)
    const index = videos.value.findIndex((item) => item.uuid === media.uuid)
    if (index >= 0) videos.value[index] = media
    else videos.value.push(media)
  } else {
    videos.value = videos.value.filter((item) => item.uuid !== media.uuid)
    const index = photos.value.findIndex((item) => item.uuid === media.uuid)
    if (index >= 0) photos.value[index] = media
    else photos.value.push(media)
  }

  imageUuid.value = uuid.v4().toString()
  syncMedia()
}

const removeMedia = (targetUuid: string) => {
  photos.value = photos.value.filter((item) => item.uuid !== targetUuid)
  videos.value = videos.value.filter((item) => item.uuid !== targetUuid)
  syncMedia()
}
</script>

<template>
  <div class="estate-project-section-body">
    <InputText type="text" name="photos" hidden />
    <InputText type="text" name="videos" hidden />

    <div>
      <label class="estate-project-section-title">Gallery ảnh / video <span>*</span></label>
      <div class="estate-project-gallery-row">
        <div class="estate-project-gallery-item" v-for="item in photos" :key="item.uuid">
          <ImagePicker
            readonly
            type="photo"
            :uuid="item.uuid"
            folder="real_estate"
            width="120px"
            height="100px"
            :src="item.url?.medium || item.name"
            :previewSrc="item.url?.original"
          />
          <button
            v-if="editable"
            type="button"
            class="estate-project-gallery-remove"
            aria-label="Xóa ảnh"
            @click="removeMedia(item.uuid)"
          >
            <Icon icon="clarity:remove-solid" width="18" height="18" />
          </button>
        </div>

        <div class="estate-project-gallery-item" v-for="item in videos" :key="`video-${item.uuid}`">
          <VideoPicker
            readonly
            type="video"
            :uuid="item.uuid"
            folder="real_estate"
            width="120px"
            height="100px"
            :src="item.url?.thumbnail || item.name"
            :previewSrc="item.url?.original"
          />
          <button
            v-if="editable"
            type="button"
            class="estate-project-gallery-remove"
            aria-label="Xóa video"
            @click="removeMedia(item.uuid)"
          >
            <Icon icon="clarity:remove-solid" width="18" height="18" />
          </button>
        </div>

        <ImagePicker
          v-if="editable"
          type="photo"
          :uuid="imageUuid"
          folder="real_estate"
          width="120px"
          height="100px"
          accept="image/*,video/*"
          @action:upload-success="upsertMedia"
          @action:choose-image="upsertMedia"
          upload
          previewIcon="sidekickicons:photo-plus-16-solid"
          hideUploadPreview
          hideRemove
        />
      </div>
      <span class="estate-project-error">
        {{ props.form?.fields?.photos?.states?.error?.message || props.form?.fields?.videos?.states?.error?.message }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.estate-project-section-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.estate-project-section-title {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin-bottom: 10px;
}

.estate-project-section-title span {
  color: #ff0000;
}

.estate-project-gallery-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 32px;
  padding: 32px 0;
}

.estate-project-gallery-item {
  position: relative;
}

.estate-project-gallery-remove {
  position: absolute;
  top: -9px;
  right: -9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #cf0000;
  padding: 0;
  z-index: 2;
}

.estate-project-error {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-style: italic;
  color: #dc2626;
}
</style>
